import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', lavender: '#A78BFA', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const CURRENCY_SYMBOL = { USD: '$', GHS: 'GHS' };

const PLAN_STYLE = {
  trial:      { accent: C.gold,     label: 'Trial'      },
  basic:      { accent: C.sky,      label: 'Basic'      },
  growth:     { accent: C.lavender, label: 'Growth'     },
  enterprise: { accent: C.gold,     label: 'Enterprise' },
  // legacy
  pro:        { accent: C.sky,      label: 'Pro'        },
  business:   { accent: C.gold,     label: 'Business'   },
};

export default function Pricing() {
  const { user, login } = useAuth();
  const navigate        = useNavigate();
  const { search }      = useLocation();
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState('');
  const [toast, setToast]     = useState(null);
  const [renewalModal, setRenewalModal] = useState(null); // { plan }

  const upgradeRequired = new URLSearchParams(search).get('upgrade') === 'required';

  const currentPlan = user?.plan || 'trial';
  const activePaidPlan = ['basic', 'growth', 'enterprise', 'pro', 'business'].includes(user?.plan)
    && (!user?.subscriptionEndDate || new Date(user.subscriptionEndDate) > new Date())
    && (!user?.subscriptionStatus || ['active', 'cancelled'].includes(user.subscriptionStatus));
  const trialDaysRemaining = user?.plan === 'trial' && user?.subscriptionEndDate
    ? Math.max(0, Math.ceil((new Date(user.subscriptionEndDate) - Date.now()) / 86400000))
    : null;
  const showToast   = (message, type = 'error') => setToast({ message, type });

  useEffect(() => {
    api.get('/subscriptions/plans')
      .then((r) => setPlans(r.data))
      .catch(() => showToast('Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgradeClick = (plan) => {
    if (activePaidPlan && plan.id === currentPlan) return;
    setRenewalModal({ plan });
  };

  const handleUpgrade = async (plan, autoRenew) => {
    setRenewalModal(null);
    setUpgrading(plan.id);
    try {
      const { data } = await api.post('/subscriptions/initialize', {
        plan: plan.id,
        autoRenew,
      });
      window.location.href = data.authorization_url;
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not start checkout');
      setUpgrading('');
    }
  };

  return (
    <Layout>
      <div style={{ padding: '20px 32px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, color: C.text, fontSize: '1.3rem', fontWeight: 800 }}>Pricing &amp; Plans</h2>
        <span style={{ fontSize: '0.8rem', color: C.muted }}>
          Upgrade to unlock more features
        </span>
      </div>

      <div style={{ padding: '28px 32px' }}>
        {/* Payment methods banner */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32, padding: '12px 18px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', color: C.muted, fontWeight: 500 }}>Accepted payments:</span>
          {[
            { icon: '📱', label: 'MTN MoMo' },
            { icon: '📱', label: 'Vodafone Cash' },
            { icon: '💳', label: 'Visa / Mastercard' },
          ].map(({ icon, label }) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', fontWeight: 500, color: C.sub, background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px' }}>
              {icon} {label}
            </span>
          ))}
        </div>

        {upgradeRequired && (
          <div style={{ marginBottom: 24, padding: '16px 18px', background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)', borderRadius: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.sky, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              Upgrade Required
            </div>
            <div style={{ color: C.text, fontWeight: 600 }}>
              The feature you tried to access requires a higher plan. Upgrade below to unlock it.
            </div>
          </div>
        )}

        {currentPlan === 'trial' && (
          <div style={{ marginBottom: 24, padding: '16px 18px', background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.25)', borderRadius: 12 }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
              14-Day Free Trial
            </div>
            <div style={{ color: C.text, fontWeight: 600 }}>
              {trialDaysRemaining > 0
                ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} left in your trial. Choose a paid plan before it ends to keep using OptiKa.`
                : 'Your trial has ended. Choose a paid plan below to continue using OptiKa.'}
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '56px 24px', color: C.muted }}>Loading plans...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, maxWidth: 900 }}>
            {plans.map((plan) => {
              const isCurrent = activePaidPlan && plan.id === currentPlan;
              const style     = PLAN_STYLE[plan.id] || PLAN_STYLE.trial;
              const symbol    = CURRENCY_SYMBOL[plan.currency] || '$';

              return (
                <div
                  key={plan.id}
                  style={{
                    border:       `2px solid ${isCurrent ? style.accent : C.border}`,
                    borderRadius: 16,
                    padding:      28,
                    background:   C.surface,
                    position:     'relative',
                    boxShadow:    isCurrent ? `0 0 0 3px ${style.accent}22` : 'none',
                    transition:   'box-shadow 0.2s',
                  }}
                >
                  {/* Current plan badge */}
                  {isCurrent && (
                    <span style={{
                      position:   'absolute', top: -13, left: '50%',
                      transform:  'translateX(-50%)',
                      background: style.accent, color: C.ink,
                      fontSize:   '0.72rem', fontWeight: 700,
                      padding:    '3px 12px', borderRadius: 20,
                      whiteSpace: 'nowrap',
                    }}>
                      Current Plan
                    </span>
                  )}

                  {/* Plan name */}
                  <div style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: style.accent, background: `${style.accent}1F`, padding: '2px 9px', borderRadius: 20 }}>
                      {style.label}
                    </span>
                  </div>

                  {/* Price */}
                  <div style={{ margin: '16px 0 20px' }}>
                    <>
                      <span style={{ fontSize: '1rem', fontWeight: 600, color: C.muted, verticalAlign: 'super' }}>{symbol}</span>
                      <span style={{ fontSize: '2.2rem', fontWeight: 800, color: C.text }}>{plan.price}</span>
                      <span style={{ fontSize: '0.85rem', color: C.muted, marginLeft: 4 }}>/ month</span>
                    </>
                  </div>

                  {/* Features */}
                  <ul style={{ listStyle: 'none', margin: '0 0 24px', padding: 0 }}>
                    {plan.features.map((f) => (
                      <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.875rem', color: C.sub, marginBottom: 8 }}>
                        <span style={{ color: C.teal, fontWeight: 700, flexShrink: 0 }}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* CTA button */}
                  {isCurrent ? (
                    <button disabled style={{ width: '100%', padding: 10, border: `1px solid ${style.accent}`, borderRadius: 10, background: `${style.accent}1F`, color: style.accent, fontWeight: 700, cursor: 'default', fontSize: '0.875rem', fontFamily: 'inherit' }}>
                      Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpgradeClick(plan)}
                      disabled={!!upgrading}
                      style={{ width: '100%', padding: 10, border: 'none', borderRadius: 10, background: C.gold, color: C.ink, fontWeight: 700, cursor: upgrading ? 'wait' : 'pointer', fontSize: '0.875rem', transition: 'opacity 0.15s', opacity: upgrading ? 0.7 : 1, fontFamily: 'inherit' }}
                    >
                      {upgrading === plan.id ? 'Redirecting...' : `Upgrade to ${plan.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Note */}
        <p style={{ marginTop: 28, fontSize: '0.8rem', color: C.muted }}>
          Every account starts with a 14-day free trial. Payments are processed securely by Paystack and subscriptions renew monthly.
        </p>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Renewal choice modal */}
      {renewalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 18, padding: 32, maxWidth: 400, width: '90%', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 800, color: C.text }}>
              Choose payment type
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.875rem', color: C.muted }}>
              Upgrading to <strong>{renewalModal.plan.name}</strong> — how would you like to pay?
            </p>

            {/* Auto-renew option */}
            <button
              onClick={() => handleUpgrade(renewalModal.plan, true)}
              style={{ width: '100%', marginBottom: 12, padding: '14px 16px', border: `2px solid ${C.gold}`, borderRadius: 12, background: 'rgba(240,180,41,0.1)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
            >
              <div style={{ fontWeight: 700, color: C.gold, marginBottom: 2 }}>Auto-renewal</div>
              <div style={{ fontSize: '0.8rem', color: C.sub }}>Card payment only · Renews automatically each month</div>
            </button>

            {/* One-time option */}
            <button
              onClick={() => handleUpgrade(renewalModal.plan, false)}
              style={{ width: '100%', marginBottom: 20, padding: '14px 16px', border: `2px solid ${C.border}`, borderRadius: 12, background: 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}
            >
              <div style={{ fontWeight: 700, color: C.text, marginBottom: 2 }}>Pay once</div>
              <div style={{ fontSize: '0.8rem', color: C.muted }}>Card, MTN MoMo, Vodafone Cash · 30-day access, manual renewal</div>
            </button>

            <button
              onClick={() => setRenewalModal(null)}
              style={{ width: '100%', padding: 10, border: 'none', borderRadius: 8, background: 'transparent', color: C.muted, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
